import React, { useState } from "react";
import { Questionnaire, QuestionnaireDefinition } from "../common/models";
import { QuestionnaireTaskView } from "../common/QuestionnaireTaskView";


const Definition: QuestionnaireDefinition = {
  name: 'GAD-7 Anxiety',
  key: 'gadAnxietyQuestionnaire',
  title: 'GAD-7 Anxiety',
  description: '',
  cohort: 'generic',
  type: 'questionnaire',
  estimatedCompletionTime: '0:50',
  groups: [
    {
      key: 'gad7',
      description: 'Over the last two weeks, how often have you been bothered by the following problems?',
      questions: [
        {
          // The key is REDCap model.
          key: 'nervousAnxious',
          type: 'multiple-choice',
          question: 'Feeling nervous, anxious, or on edge.',
          options: [
            { key: '0', displayName: 'Not at all' },
            { key: '1', displayName: 'Several days' },
            { key: '2', displayName: 'More than half the days' },
            { key: '3', displayName: 'Nearly every day' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        },
        {
          // The key is REDCap model.
          key: 'cantControlWorry',
          type: 'multiple-choice',
          question: 'Not being able to stop or control worrying.',
          options: [
            { key: '0', displayName: 'Not at all' },
            { key: '1', displayName: 'Several days' },
            { key: '2', displayName: 'More than half the days' },
            { key: '3', displayName: 'Nearly every day' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        },
        {
          // The key is REDCap model.
          key: 'worryTooMuch',
          type: 'multiple-choice',
          question: 'Worrying too much about different things.',
          options: [
            { key: '0', displayName: 'Not at all' },
            { key: '1', displayName: 'Several days' },
            { key: '2', displayName: 'More than half the days' },
            { key: '3', displayName: 'Nearly every day' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        },
        {
          // The key is REDCap model.
          key: 'troubleRelaxing',
          type: 'multiple-choice',
          question: 'Trouble relaxing.',
          options: [
            { key: '0', displayName: 'Not at all' },
            { key: '1', displayName: 'Several days' },
            { key: '2', displayName: 'More than half the days' },
            { key: '3', displayName: 'Nearly every day' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        },
        {
          // The key is REDCap model.
          key: 'hardToSitStill',
          type: 'multiple-choice',
          question: 'Being so restless that it is hard to sit still.',
          options: [
            { key: '0', displayName: 'Not at all' },
            { key: '1', displayName: 'Several days' },
            { key: '2', displayName: 'More than half the days' },
            { key: '3', displayName: 'Nearly every day' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        },
        {
          // The key is REDCap model.
          key: 'easilyAgitated',
          type: 'multiple-choice',
          question: 'Becoming easily annoyed or irritable.',
          options: [
            { key: '0', displayName: 'Not at all' },
            { key: '1', displayName: 'Several days' },
            { key: '2', displayName: 'More than half the days' },
            { key: '3', displayName: 'Nearly every day' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        },
        {
          // The key is REDCap model.
          key: 'afraidOfThings',
          type: 'multiple-choice',
          question: 'Feeling afraid, as if something awful might happen.',
          options: [
            { key: '0', displayName: 'Not at all' },
            { key: '1', displayName: 'Several days' },
            { key: '2', displayName: 'More than half the days' },
            { key: '3', displayName: 'Nearly every day' }
          ],
          value:  '',
          defaultValue: '',
          required: true
        },
        {
          // The key is from REDCap.
          key: 'selection',
          type: 'multiple-choice',
          question: 'How difficult have they made it for you to do your work, take care of things at home, or get along with other people?',
          options: [
            { key: '0', displayName: 'Not difficult at all' },
            { key: '1', displayName: 'Somewhat difficult' },
            { key: '2', displayName: 'Very difficult' },
            { key: '3', displayName: 'Extremely difficult' }
          ],
          value:  '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'gad7');
            const question1 = group?.questions.find(x => x.key === 'nervousAnxious');
            const question2 = group?.questions.find(x => x.key === 'cantControlWorry');
            const question3 = group?.questions.find(x => x.key === 'worryTooMuch');
            const question4 = group?.questions.find(x => x.key === 'troubleRelaxing');
            const question5 = group?.questions.find(x => x.key === 'hardToSitStill');
            const question6 = group?.questions.find(x => x.key === 'easilyAgitated');
            const question7 = group?.questions.find(x => x.key === 'afraidOfThings');
            const question1Value = question1?.value ? Number(question1.value) : null;
            const question2Value = question2?.value ? Number(question2.value) : null;
            const question3Value = question3?.value ? Number(question3.value) : null;
            const question4Value = question4?.value ? Number(question4.value) : null;
            const question5Value = question5?.value ? Number(question5.value) : null;
            const question6Value = question6?.value ? Number(question6.value) : null;
            const question7Value = question7?.value ? Number(question7.value) : null;

            return (question1Value !== null && question1Value > 0) ||
            (question2Value !== null && question2Value > 0) ||
            (question3Value !== null && question3Value > 0) ||
            (question4Value !== null && question4Value > 0) ||
            (question5Value !== null && question5Value > 0) ||
            (question6Value !== null && question6Value > 0) ||
            (question7Value !== null && question7Value > 0);
          }
        }
      ]
    }
  ]
};

export const GAD7Definition = Definition;

export const GAD7 = ({onCompletion}: {onCompletion: () => void}) => {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>(() => ({
    id: 'gadAnxietyQuestionnaires',
    ...Definition,
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'in-progress',
  }));

  console.log('GAD7 component rendered', questionnaire);

  const handleOnCompletion = () => {
    console.log('GAD7 completed');

    onCompletion();
  };

  const handleOnChange = (questionnaire: Questionnaire) => {
    console.log('questionnaire', questionnaire);
    setQuestionnaire(questionnaire);
  }

  console.log('GAD7 component rendered', questionnaire);

  return (
    <div>
      <QuestionnaireTaskView questionnaire={questionnaire} onChange={handleOnChange} onCompletion={handleOnCompletion  } />
    </div>
  );
}
